import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment.development';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

const MAX_ATTEMPTS = 2;

@Injectable({ providedIn: 'root' })
export class TrackingSignalrService {
  private hub?: signalR.HubConnection;
  private heartbeat?: ReturnType<typeof setInterval>;
  private gen = 0;
  private currentCity?: string;
  private currentStoreId?: string;
  private pendingActions: Array<() => Promise<void>> = [];
  public readonly initialDrivers$ = new Subject<any[]>();
  public readonly driverMoved$ = new Subject<any>();
  public readonly clearDrivers$ = new Subject<void>();
  public readonly connectionReady$ = new Subject<void>();
  public readonly reconnected$ = new Subject<void>();
  public readonly connectionStatus$ = new BehaviorSubject<ConnectionStatus>('disconnected');
  public readonly isRetrying$ = new BehaviorSubject<boolean>(false);
  public readonly tripStatus$ = new Subject<{ eventName: string; data: any }>();
  public readonly newFoodOrder$ = new Subject<any>();
  public readonly foodOrderCancelled$ = new Subject<any>();
  public readonly foodOrderExpired$ = new Subject<any>();
  public URL = environment.apiSocket;

  public ensureConnected(token: string): void {
    if (this.hub && this.hub.state !== signalR.HubConnectionState.Disconnected) return;
    this.startConnection(token);
  }

  public startConnection(token: string): void {
    this.teardown();
    const gen = ++this.gen;
    

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(this.URL, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ctx => {
          if (ctx.elapsedMilliseconds < 60_000) return 2_000;
          if (ctx.elapsedMilliseconds < 300_000) return 10_000;
          return 30_000;
        },
      })
      .build();

    hub.onreconnecting(() => {
      if (gen !== this.gen) return;
      this.connectionStatus$.next('reconnecting');
    });

    hub.onreconnected(() => {
      if (gen !== this.gen) return;
      this.connectionStatus$.next('connected');
      this.reconnected$.next();

      this.flushQueue();
      this.rejoinGroups();
    });

    hub.onclose(() => {
      if (gen !== this.gen) return;
      this.connectionStatus$.next('disconnected');
      this.stopHeartbeat();
    });

    hub.on('InitialDriversLoad', (data: any[]) => {
      if (gen !== this.gen) return;
      this.initialDrivers$.next(data);
    });

    hub.on('DriverMoved', (data: any) => {
      if (gen !== this.gen) return;
      this.driverMoved$.next(data);
    });

    hub.on('TripStatusUpdated', (eventName: string, data: any) => {
      console.log('Evento de estado de viaje recibido:', eventName, data);

      if (gen !== this.gen) return;
      this.tripStatus$.next({ eventName, data });
    });

    hub.on('JoinedTripTracking', (data: any) => {
      console.log('Confirmación de unión a seguimiento de viaje recibida:', data);
    });

    hub.on('StoppedTripTracking', (data: any) => {
      console.log('Confirmación de salida de seguimiento de viaje recibida:', data);
    });

    hub.on('NewFoodOrder', (data: any) => {
      if (gen !== this.gen) return;
      this.newFoodOrder$.next(data);
    });

    hub.on('FoodOrderCancelled', (data: any) => {
      if (gen !== this.gen) return;
      this.foodOrderCancelled$.next(data);
    });

    hub.on('FoodOrderExpired', (data: any) => {
      if (gen !== this.gen) return;
      this.foodOrderExpired$.next(data);
    });

    hub.on('JoinedStoreNotifications', (data: any) => {
      console.log('Confirmación de unión a notificaciones de tienda:', data);
    });

    hub.on('StoreOpened', (data: any) => {
      console.log('Confirmación de apertura de tienda:', data);
    });

    hub.on('StoreClosed', (data: any) => {
      console.log('Confirmación de cierre de tienda:', data);
    });

    hub.on('Error', (message: string) => {
      console.error('Error del servidor:', message);
    });

    this.hub = hub;
    this.connectWithRetry(gen, hub);
  }

  public retry(token: string): void {
    this.startConnection(token);
  }

  private async connectWithRetry(gen: number, hub: signalR.HubConnection): Promise<void> {
    this.isRetrying$.next(true);

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      if (gen !== this.gen) return;

      try {
        await hub.start();
        
        if (gen !== this.gen) return;

        this.connectionStatus$.next('connected');
        this.connectionReady$.next();
        this.startHeartbeat(gen);
        this.isRetrying$.next(false);
        this.flushQueue();
        this.rejoinGroups();

        return;
      } catch {
        if (gen !== this.gen) return;

        if (attempt < MAX_ATTEMPTS) {
          const delay = Math.min(2000 * attempt, 30000);
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }

    if (gen !== this.gen) return;
    this.isRetrying$.next(false);
  }

  private async safeInvoke(method: string, ...args: any[]): Promise<void> {
    if (!this.hub) {
      console.warn(`Hub no inicializado para método ${method}`);
      return;
    }

    if (this.hub.state !== signalR.HubConnectionState.Connected) {
      console.log(`Hub no conectado (${this.hub.state}), encolando ${method}`);
      this.pendingActions.push(() => this.safeInvoke(method, ...args));
      return;
    }

    try {
      console.log(`Invocando ${method} con argumentos:`, args);
      await this.hub.invoke(method, ...args);
      console.log(`${method} invocado exitosamente`);
    } catch (err) {
      console.error(`Error en invoke ${method}:`, err);
      throw err;
    }
  }

  private flushQueue(): void {
    const actions = [...this.pendingActions];
    this.pendingActions = [];

    actions.forEach(a => {
      a().catch(err => console.error('Error ejecutando acción encolada:', err));
    });
  }

  private rejoinGroups(): void {
    if (this.currentCity) {
      this.safeInvoke('JoinAdminDashboard', this.currentCity);
    }
    if (this.currentStoreId) {
      this.safeInvoke('JoinStoreNotifications', this.currentStoreId);
    }
  }

  public joinAdminDashboard(ciudad: string): void {
    this.currentCity = ciudad;
    this.safeInvoke('JoinAdminDashboard', ciudad);
  }

  public leaveAdminDashboard(ciudad: string): void {
    this.safeInvoke('LeaveAdminDashboard', ciudad);
  }

  public switchCity(oldCiudad: string, newCiudad: string): void {
    this.clearDrivers$.next();

    this.currentCity = newCiudad;

    this.safeInvoke('LeaveAdminDashboard', oldCiudad);
    this.safeInvoke('JoinAdminDashboard', newCiudad);
  }

  public joinStoreNotifications(storeId: string): void {
    this.currentStoreId = storeId;
    this.safeInvoke('JoinStoreNotifications', storeId);
  }

  public openStore(): Promise<void> {
    return this.safeInvoke('OpenStore');
  }

  public closeStore(): Promise<void> {
    return this.safeInvoke('CloseStore');
  }

  public joinTripTracking(tripId: string): void {
    console.log('Uniéndose al seguimiento del viaje:', tripId);
    console.log('Estado actual del hub:', this.hub?.state);
    console.log('Hub conectado?', this.hub?.state === signalR.HubConnectionState.Connected);
    this.safeInvoke('JoinTripTracking', tripId);
  }

  public leaveTripTracking(tripId: string): void {
    this.safeInvoke('LeaveTripTracking', tripId);
  }

  private startHeartbeat(gen: number): void {
    this.stopHeartbeat();

    this.heartbeat = setInterval(() => {
      if (gen !== this.gen) {
        this.stopHeartbeat();
        return;
      }

      if (this.hub?.state === signalR.HubConnectionState.Connected) {
        this.hub.invoke('Ping').catch(() => {});
      }
    }, 25000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== undefined) {
      clearInterval(this.heartbeat);
      this.heartbeat = undefined;
    }
  }

  private teardown(): void {
    this.gen++;
    this.stopHeartbeat();

    const old = this.hub;
    this.hub = undefined;

    this.pendingActions = [];
    this.isRetrying$.next(false);

    old?.stop();
  }

  public stopConnection(): void {
    this.teardown();
    this.connectionStatus$.next('disconnected');
  }
}